import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiClientV2 } from "../../../services/api/apiClient"
import { isFeatureDisabledError, toastApiError } from "./apiError"
import { invalidateDeclaration } from "./declarationsApi"

const BASE = "/material-compliance/declarations"

/** One box of a marked passage, fractions of the page (0..1). */
export interface QuestionBox {
  x: number
  y: number
  w: number
  h: number
}

/** A collected review question: free text at a statement, or a marked PDF
 *  passage (`page`/`quote`/`boxes` present). Camel-case on the wire. */
export interface DeclarationQuestion {
  id: string
  framework: string | null
  statementId: string | null
  page: number | null
  quote: string | null
  boxes: QuestionBox[] | null
  question: string
  position: number
  inquiryId: string | null
}

/** Dense 1..n rank for each question's stored `position`, ascending: the
 *  SAME renumbering the backend's own `questions_list_block` already applies
 *  (`mc_declaration_questions.py`: sort by `position`, then `enumerate(start=1)`).
 *  A deleted middle question leaves a gap in the stored positions (1, 2, 4
 *  for three survivors); every surface that numbers a question (the PDF
 *  badges, the request dialog's sidebar/preview lists) must renumber densely
 *  off this SAME order rather than echo the raw `position`, or it shows "4"
 *  for what the mail body already calls "3" (live test L5, wave 5). Keyed on
 *  `id`, not `position`, purely so no caller reuses a raw number as a map
 *  key; `position` itself is already unique per declaration (assigned as
 *  `max + 1` on create, `mc_declaration_question_repository.py`). */
export function densePositionsOf(
  items: { id: string; position: number }[],
): Map<string, number> {
  return new Map(
    [...items]
      .sort((a, b) => a.position - b.position)
      .map((item, i) => [item.id, i + 1]),
  )
}

export interface CreateQuestionIn {
  framework?: string | null
  statementId?: string | null
  page?: number | null
  quote?: string | null
  boxes?: QuestionBox[] | null
  question: string
}

export interface QuestionDraft {
  subject: string
  body: string
  language: string
  source: "ai" | "template"
  /** The backend's own numbered list block (`QuestionDraftOut.list_block`),
   *  always present on the wire; the read-only preview shows it verbatim
   *  instead of re-deriving it client-side. */
  listBlock: string
}

export interface SendQuestionsIn {
  subject: string
  body: string
  recipientsTo?: string[] | null
  recipientsCc?: string[] | null
  /** MC_NOTES: the supplier's warning notes the user confirmed (see isSupplierWarningError). */
  acknowledgedNoteIds?: string[]
}

export interface SendQuestionsOut {
  inquiryId: string
  reference: string | null
  attachment: { fileName: string } | null
}

const questionsKey = (declId: string, sent?: boolean) =>
  ["mc", "declarations", declId, "questions", sent ?? "all"] as const

/** GET /declarations/{id}/questions?sent=: drafts (sent=false, default) or
 *  already-sent items (sent=true). Declaration-scoped: callers filter down
 *  to a card's own `framework`/`statementId` locally. */
export function useDeclarationQuestions(declId: string, sent?: boolean) {
  return useQuery({
    queryKey: questionsKey(declId, sent),
    queryFn: async (): Promise<DeclarationQuestion[]> => {
      return apiClientV2
        .get(`${BASE}/${declId}/questions`, {
          params: sent === undefined ? undefined : { sent },
        })
        .then((r) =>
          Array.isArray(r.data) ? (r.data as DeclarationQuestion[]) : [],
        )
    },
    enabled: !!declId,
  })
}

function invalidateQuestions(
  queryClient: ReturnType<typeof useQueryClient>,
  declId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: ["mc", "declarations", declId, "questions"],
  })
}

export function useCreateQuestion(declId: string) {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: async (
      input: CreateQuestionIn,
    ): Promise<DeclarationQuestion> => {
      return apiClientV2
        .post(`${BASE}/${declId}/questions`, input)
        .then((r) => r.data as DeclarationQuestion)
    },
    onSuccess: () => invalidateQuestions(queryClient, declId),
    // THE ONE handler for this mutation, call sites included: React Query runs
    // a per-call `onError` in ADDITION to this one, so a second toast at a
    // call site would contradict the single cause named here.
    //
    // Keyed on the flag dependency's own `error_code`, not on the status: the
    // route carries `require_flag('MC_DECLARATION_INQUIRY')` AND the review
    // permission, so a 403 is not uniquely the disabled feature, and naming
    // the feature for a denied permission sends the reviewer to ask for the
    // wrong thing. The generic message alone left a reviewer retrying a
    // gesture that cannot succeed (a live session produced twelve refusals),
    // which is why the flag case keeps its own copy.
    onError: (error) =>
      toastApiError(
        error,
        translate,
        isFeatureDisabledError(error)
          ? "MC_DECL_V2_QUESTIONS_NOT_ENABLED"
          : "MC_API_ERROR_GENERIC",
      ),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

export function useUpdateQuestion(declId: string) {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: async (input: {
      questionId: string
      question: string
    }): Promise<void> => {
      await apiClientV2.patch(
        `${BASE}/${declId}/questions/${input.questionId}`,
        {
          question: input.question,
        },
      )
    },
    onSuccess: () => invalidateQuestions(queryClient, declId),
    onError: (error) => toastApiError(error, translate, "MC_API_ERROR_GENERIC"),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

export function useDeleteQuestion(declId: string) {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: async (questionId: string): Promise<void> => {
      await apiClientV2.delete(`${BASE}/${declId}/questions/${questionId}`)
    },
    onSuccess: () => invalidateQuestions(queryClient, declId),
    onError: (error) => toastApiError(error, translate, "MC_API_ERROR_GENERIC"),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

/** POST /declarations/{id}/questions/draft: never stores anything server
 *  side, so this is a mutation (an on-demand compute), not a query. */
export function useQuestionDraft(declId: string) {
  const mutation = useMutation({
    mutationFn: async (): Promise<QuestionDraft> => {
      return apiClientV2
        .post(`${BASE}/${declId}/questions/draft`, {})
        .then((r) => r.data as QuestionDraft)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}

/** POST /declarations/{id}/questions/send: one declaration-scoped inquiry
 *  for every open draft; success invalidates both the questions list and the
 *  declaration prefix (the framework rows' inquiry state changes too). */
export function useSendQuestions(declId: string) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (input: SendQuestionsIn): Promise<SendQuestionsOut> => {
      return apiClientV2
        .post(`${BASE}/${declId}/questions/send`, input)
        .then((r) => r.data as SendQuestionsOut)
    },
    onSuccess: () => {
      invalidateQuestions(queryClient, declId)
      invalidateDeclaration(queryClient, declId)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_DECLARATIONS_REVIEW)
}
